import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { MAIN_SITE_URL, categories } from "@/data/categories";
import { useClickOutside } from "@/hooks/useClickOutside";

const languages = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "pl", label: "Polski", flag: "🇵🇱" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
];

const navLinks = [
  { key: "home", href: "/" },
  { key: "spreadsheets", href: "/#spreadsheet" },
  { key: "products", href: "/#categories" },
  { key: "sellers", href: "/#categories" },
  { key: "reviews", href: "/#review" },
  { key: "shipping", href: "/#shipping" },
  { key: "coupons", href: "/#resources" },
  { key: "guides", href: "/#guides" },
  { key: "safety", href: "/#safety" },
];

export default function SiteNav() {
  const { t } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useClickOutside(langRef, () => setLangOpen(false));

  const currentLang = languages.find((l) => l.code === i18n.language) ?? languages[0];

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    localStorage.setItem("i18nextLng", code);
    setLangOpen(false);
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b-2 border-background-300 bg-background-50">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-table-line text-lg" />
          </span>
          <span className="font-heading text-lg font-bold text-foreground-950">{t("brand")}</span>
        </Link>

        <nav className="hidden items-center gap-5 lg:flex">
          {navLinks.map((link) => (
            <Link
              key={link.key}
              to={link.href}
              className="whitespace-nowrap text-sm font-semibold text-foreground-800 transition-colors hover:text-primary-600"
            >
              {t(`nav.${link.key}`)}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/usfans-spreadsheet-guide"
            className="hidden whitespace-nowrap rounded-full border border-accent-300 bg-accent-100 px-4 py-2 text-sm font-semibold text-accent-900 transition-colors hover:bg-accent-200 lg:inline-flex"
          >
            {t("nav.shoppingGuide")}
          </Link>

          <div className="relative" ref={langRef}>
            <button
              type="button"
              onClick={() => setLangOpen((v) => !v)}
              aria-label={t("nav.chooseLanguage")}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
            >
              <span className="text-base leading-none">{currentLang.flag}</span>
              <span className="hidden sm:inline">{currentLang.label}</span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-40 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => changeLanguage(lang.code)}
                    className={`flex w-full cursor-pointer items-center gap-2 whitespace-nowrap px-4 py-2.5 text-left text-sm transition-colors hover:bg-background-100 ${
                      lang.code === currentLang.code ? "font-semibold text-primary-700" : "text-foreground-700"
                    }`}
                  >
                    <span className="text-base leading-none">{lang.flag}</span>
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="hidden whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            {t("nav.buyNow")}
          </a>

          <button
            type="button"
            aria-label="Menu"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg text-foreground-900 lg:hidden"
          >
            <i className={`text-2xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="max-h-[80vh] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
          <nav className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.key}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className="whitespace-nowrap rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
              >
                {t(`nav.${link.key}`)}
              </Link>
            ))}
            <Link
              to="/usfans-spreadsheet-guide"
              onClick={() => setMobileOpen(false)}
              className="whitespace-nowrap rounded-lg px-3 py-2.5 text-sm font-semibold text-accent-800 transition-colors hover:bg-accent-100"
            >
              {t("nav.shoppingGuide")}
            </Link>
          </nav>

          <div className="mt-4 border-t border-background-200 pt-4">
            <p className="px-3 text-xs font-bold uppercase tracking-wide text-foreground-500">{t("nav.categories")}</p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-2 transition-colors hover:border-primary-300"
                >
                  <img
                    src={cat.image}
                    alt={t(`categories.${cat.key}`)}
                    className="h-10 w-10 shrink-0 rounded-md object-cover object-top"
                  />
                  <span className="truncate text-sm font-medium text-foreground-800">{t(`categories.${cat.key}`)}</span>
                </a>
              ))}
            </div>
          </div>

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="mt-4 inline-flex w-full items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            {t("nav.buyNow")}
          </a>
        </div>
      )}
    </header>
  );
}